import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PortalContext = createContext(null);

const STORAGE_KEY = "fundle_portal";
const DEFAULT_CODE = "all";

// Canonical labels used when portal='all' or when a portal has no matching head.
const CANONICAL_LABELS = {
  commission: "Commission",
  gt: "GT Charge",
  fixed: "Fixed Fee",
  returnFee: "Return Fee",
  other: "Other Charges",
};

const SLOT_ORDER = ["commission", "gt", "fixed", "returnFee"];
const cleanLabel = (l) => (l || "").replace(/\s*%\s*/g, " ").replace(/\(\s*\)/g, "").replace(/\s+/g, " ").trim();
const slotFor = (l) => {
  const lbl = (l || "").toLowerCase();
  if (lbl.includes("commission")) return "commission";
  if (lbl.includes("logistic") || /\bgt\b/.test(lbl)) return "gt";
  if (lbl.includes("fixed")) return "fixed";
  if (lbl.includes("return")) return "returnFee";
  return "other";
};

function _feeLabelsFor(portal) {
  if (!portal || !Array.isArray(portal.fee_heads)) return { ...CANONICAL_LABELS };
  const out = { ...CANONICAL_LABELS };
  const others = [];
  const seen = new Set();
  for (const h of portal.fee_heads) {
    const slot = slotFor(h.label);
    if (slot === "other") others.push(cleanLabel(h.label));
    else if (!seen.has(slot) && SLOT_ORDER.includes(slot)) { out[slot] = cleanLabel(h.label); seen.add(slot); }
  }
  if (others.length) out.other = others.join(" + ");
  return out;
}

export function PortalProvider({ children }) {
  const { user } = useAuth();
  const [portals, setPortals] = useState([]);
  const [portalCode, setPortalCodeState] = useState(() => localStorage.getItem(STORAGE_KEY) || DEFAULT_CODE);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portals");
      setPortals(Array.isArray(data) ? data : []);
    } catch (e) {
      /* ignored — unauth pages hit this too */
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload whenever auth user changes (login / logout). Covers "empty after login" bug.
  useEffect(() => {
    if (user) load(); else setPortals([]);
  }, [user, load]);

  const setPortalCode = useCallback((code) => {
    const val = (code || DEFAULT_CODE).toLowerCase();
    localStorage.setItem(STORAGE_KEY, val);
    setPortalCodeState(val);
  }, []);

  const portalParam = portalCode === "all" ? undefined : portalCode;
  const active = portals.find((p) => p.code === portalCode);
  // Fee-head labels for the current portal. `feeLabels` gives portal-specific names
  // (e.g. Amazon: "Logistic %"); falls back to canonical names when portal='all'.
  // `feeLabelsFor(code)` lets a specific row look up a different portal's labels
  // (used by Sales Ledger rows when portal='all' — each row shows its own
  // portal's naming).
  const feeLabels = useMemo(() => _feeLabelsFor(active), [active]);
  const feeLabelsFor = useCallback(
    (code) => _feeLabelsFor(portals.find((p) => p.code === (code || "").toLowerCase())),
    [portals]
  );

  return (
    <PortalContext.Provider value={{
      portals, portalCode, setPortalCode, portalParam, active, loading, reload: load,
      feeLabels, feeLabelsFor,
    }}>
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error("usePortal must be used within PortalProvider");
  return ctx;
}
